import { FC, useEffect, useRef, useState } from 'react'
import { getUserCompanies } from './header-menus/core/_requests'
import { updateOperator } from '../../../../app/modules/apps/operators-management/operators-list/core/_requests'
import { UserCompany, UserCompaniesQueryResponse } from './header-menus/core/_models'
import { getAuth, setAuth } from '../../../../app/modules/auth/core/AuthHelpers'
import { AuthModel } from '../../../../app/modules/auth/core/_models'
import { useAuth } from '../../../../app/modules/auth'
import { Operator } from '../../../../app/modules/apps/operators-management/operators-list/core/_models'
import { trigger } from '../../../../app/helpers/events'
import { COMPANIES_REFRESH } from '../../../../app/modules/apps/companies-management/companies-list/CompaniesList'
import { OPERATORS_REFRESH } from '../../../../app/modules/apps/operators-management/operators-list/OperatorsList'
import { COORDINATORS_REFRESH } from '../../../../app/modules/apps/coordinators-management/coordinator-list/CoordinatorsList'
import { MANAGERS_REFRESH } from '../../../../app/modules/apps/managers-management/managers-list/ManagersList'
import { ADMINISTRATORS_REFRESH } from '../../../../app/modules/apps/administrators-management/administrators-list/AdministratorsList'
import { MEASURING_BOXES_REFRESH } from '../../../../app/modules/apps/measuring-box-management/measuring-box-list/List'
import { PROJECTS_REFRESH } from '../../../../app/modules/apps/projects-management/projects-list/List'
import { LOGS_BOX_REFRESH } from '../../../../app/modules/apps/logs-management/logs-box-list/List'
import { TOKENS } from '../../../../app/components/design-system'

export const CurrentUserCompany: FC = () => {
  const [companies, setCompanies] = useState<UserCompany[]>()
  const [open, setOpen] = useState(false)
  const [hover, setHover] = useState(false)
  const [newUserCompany, setNewUserCompany] = useState<UserCompany>()
  const [companiesLoading, setCompaniesLoading] = useState(false)
  const wrapperRef = useRef<HTMLDivElement>(null)
  const {currentUser, setCurrentUser} = useAuth()

  const togglePicker = async () => {
    const next = !open
    setOpen(next)
    if (next) {
      try {
        setCompaniesLoading(true)
        const res = await getUserCompanies(currentUser!.id) as UserCompaniesQueryResponse
        setCompanies(res.data)
      } catch {
        setCompanies([])
      } finally {
        setCompaniesLoading(false)
      }
    }
  }

  // Close picker on outside click — tokenized pickers feel premium when they
  // dismiss like real dropdowns instead of staying anchored open.
  useEffect(() => {
    if (!open) return
    const onClick = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [open])

  useEffect(() => {
    setNewUserCompany({
      id: currentUser?.last_company_id,
      name: currentUser?.last_company_name,
      show_menu_map: currentUser?.last_company_show_menu_map,
      show_menu_sensors: currentUser?.last_company_show_menu_sensors,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const changeUserCompany = (e?: React.FormEvent) => {
    e?.preventDefault()
    try {
      const currentAuth = getAuth()
      if (newUserCompany?.id && newUserCompany?.name && currentAuth) {
        const newAuth: AuthModel = {
          ...currentAuth,
          user: {
            ...currentAuth.user,
            last_company_id: newUserCompany.id,
            last_company_name: newUserCompany.name,
            last_company_show_menu_map: newUserCompany.show_menu_map,
            last_company_show_menu_sensors: newUserCompany.show_menu_sensors,
          },
        }
        setAuth(newAuth)
        setCurrentUser({
          ...currentAuth.user,
          last_company_id: newUserCompany.id,
          last_company_name: newUserCompany.name,
          last_company_show_menu_map: newUserCompany.show_menu_map,
          last_company_show_menu_sensors: newUserCompany.show_menu_sensors,
        })
        updateOperator({ id: currentUser?.id, last_company: newUserCompany.id } as Operator).then(() => {
          trigger(COMPANIES_REFRESH)
          trigger(OPERATORS_REFRESH)
          trigger(COORDINATORS_REFRESH)
          trigger(MANAGERS_REFRESH)
          trigger(ADMINISTRATORS_REFRESH)
          trigger(MEASURING_BOXES_REFRESH)
          trigger(PROJECTS_REFRESH)
          trigger(LOGS_BOX_REFRESH)
        })
      }
    } catch (ex) {
      console.error(ex)
    } finally {
      setOpen(false)
    }
  }

  const currentName = currentUser?.last_company_name ?? 'Empresa não definida'
  const initial = currentName?.[0]?.toUpperCase() ?? '?'

  return (
    <div ref={wrapperRef} style={{ position: 'relative' }}>
      <button
        type='button'
        onClick={togglePicker}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: TOKENS.spacing.sm,
          background: open || hover ? TOKENS.color.surface : TOKENS.color.card,
          border: `1px solid ${TOKENS.color.border}`,
          borderRadius: TOKENS.radius.card,
          padding: '8px 12px',
          cursor: 'pointer',
          boxShadow: TOKENS.shadow.card,
          transition: 'background 120ms ease',
        }}
        title='Trocar empresa'
      >
        <span style={{
          width: 32, height: 32, borderRadius: '50%',
          background: TOKENS.color.actionSoft, color: TOKENS.color.action,
          fontSize: 13, fontWeight: 700,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          {initial}
        </span>
        <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', lineHeight: 1.2 }}>
          <span style={{
            fontSize: TOKENS.typography.label.size,
            fontWeight: TOKENS.typography.label.weight,
            textTransform: TOKENS.typography.label.transform,
            letterSpacing: TOKENS.typography.label.tracking,
            color: TOKENS.color.muted,
          }}>
            Empresa
          </span>
          <span style={{
            fontSize: 13, fontWeight: 700, color: TOKENS.color.text,
            maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {currentName}
          </span>
        </span>
        <span style={{ color: TOKENS.color.muted, fontSize: 12, marginLeft: 2 }}>▾</span>
      </button>

      {open && (
        <form
          onSubmit={changeUserCompany}
          style={{
            position: 'absolute',
            right: 0,
            top: 'calc(100% + 6px)',
            width: 300,
            background: TOKENS.color.card,
            border: `1px solid ${TOKENS.color.border}`,
            borderRadius: TOKENS.radius.card,
            boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
            zIndex: 1050,
            overflow: 'hidden',
          }}
        >
          <div style={{
            padding: '10px 14px',
            borderBottom: `1px solid ${TOKENS.color.border}`,
            fontSize: TOKENS.typography.label.size,
            fontWeight: TOKENS.typography.label.weight,
            textTransform: TOKENS.typography.label.transform,
            letterSpacing: TOKENS.typography.label.tracking,
            color: TOKENS.color.muted,
          }}>
            Selecionar empresa
          </div>

          <div style={{ maxHeight: 280, overflowY: 'auto', padding: 4 }}>
            {companiesLoading ? (
              <div style={{ padding: TOKENS.spacing.lg, display: 'flex', justifyContent: 'center' }}>
                <div className='spinner-border spinner-border-sm text-secondary' />
              </div>
            ) : !companies?.length ? (
              <div style={{ padding: TOKENS.spacing.lg, fontSize: 12, color: TOKENS.color.muted }}>
                Nenhuma empresa vinculada.
              </div>
            ) : (
              companies.map((company) => {
                const isSelected = company.id === newUserCompany?.id
                return (
                  <label
                    key={String(company.id)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: TOKENS.spacing.sm,
                      padding: '8px 10px',
                      borderRadius: TOKENS.radius.chip,
                      background: isSelected ? TOKENS.color.actionSoft : 'transparent',
                      cursor: 'pointer',
                      transition: 'background 80ms ease',
                    }}
                    onMouseEnter={(e) => { if (!isSelected) (e.currentTarget as HTMLLabelElement).style.background = TOKENS.color.surface }}
                    onMouseLeave={(e) => { if (!isSelected) (e.currentTarget as HTMLLabelElement).style.background = 'transparent' }}
                  >
                    <input
                      type='radio'
                      name='company'
                      value={String(company.id)}
                      checked={isSelected}
                      onChange={() => setNewUserCompany({
                        id: company.id,
                        name: company.name,
                        show_menu_map: company.show_menu_map,
                        show_menu_sensors: company.show_menu_sensors,
                      })}
                      style={{ accentColor: TOKENS.color.action }}
                    />
                    <span style={{
                      fontSize: 13,
                      fontWeight: isSelected ? 700 : 500,
                      color: isSelected ? TOKENS.color.action : TOKENS.color.text,
                    }}>
                      {company.name}
                    </span>
                  </label>
                )
              })
            )}
          </div>

          <div style={{
            padding: 10,
            borderTop: `1px solid ${TOKENS.color.border}`,
            display: 'flex',
            justifyContent: 'flex-end',
            gap: TOKENS.spacing.sm,
            background: TOKENS.color.surface,
          }}>
            <button
              type='button'
              onClick={() => setOpen(false)}
              style={{
                border: 'none',
                background: 'transparent',
                color: TOKENS.color.muted,
                padding: '6px 12px',
                borderRadius: TOKENS.radius.chip,
                fontSize: 12,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Cancelar
            </button>
            <button
              type='submit'
              disabled={!companies?.length}
              style={{
                border: 'none',
                background: companies?.length ? TOKENS.color.action : TOKENS.color.border,
                color: companies?.length ? '#fff' : TOKENS.color.muted,
                padding: '6px 14px',
                borderRadius: TOKENS.radius.chip,
                fontSize: 12,
                fontWeight: 700,
                letterSpacing: 0.3,
                cursor: companies?.length ? 'pointer' : 'not-allowed',
              }}
            >
              Selecionar
            </button>
          </div>
        </form>
      )}
    </div>
  )
}
